import { useAppState } from 'hooks/useAppState';
import Index from 'pages/Index';
import Error404 from 'pages/error/Error404';
import InvitationIndex from 'pages/invitation/InvitationIndex';
import InvitationUser from 'pages/invitation/InvitationUser';
import ProxyLogin from 'pages/proxy/ProxyLogin';
import ProxyCallback from 'pages/proxy/ProxyCallback';
import SettingCompany from 'pages/setting/SettingCompany';
import SettingMember from 'pages/setting/SettingMember';
import SettingMemberEdit from 'pages/setting/SettingMemberEdit';
import SettingProfile from 'pages/setting/SettingProfile';
import SettingLayout from 'pages/setting/component/layout/SettingLayout';
import Signin from 'pages/signin/Signin';
import SigninAuth from 'pages/signin/SigninAuth';
import SigninCompany from 'pages/signin/SigninCompany';
import Signup from 'pages/signup/Signup';
import SignupCompanyOld from 'pages/signup_old/SignupCompany_old';
import SignupOld from 'pages/signup_old/Signup_old';
import WsContentSearch, { default as WsQuiz } from 'pages/ws/WsContentSearch';
import WsDiagnosisEdit from 'pages/ws/WsDiagnosisEdit';
import WsIndex from 'pages/ws/WsIndex';
import WsQuizEdit from 'pages/ws/WsQuizEdit';
import WsReportDiagnosis from 'pages/ws/WsReportDiagnosis';
import WsReportQuiz from 'pages/ws/WsReportQuiz';
import WsReportSearch from 'pages/ws/WsReportSearch';
import WsRespondentDiagnosis from 'pages/ws/WsRespondentDiagnosis';
import WsRespondentQuiz from 'pages/ws/WsRespondentQuiz';
import WsRespondentSearch from 'pages/ws/WsRespondentSearch';
import WsLayout from 'pages/ws/component/layout/WsLayout';
import { useLayoutEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { AcctState } from 'stores/atoms/acctState';
/**
 *　アプリケーション共通 Router定義
 */
export default function AppRoutes(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const location = useLocation();
  const navigate = useNavigate();

  const [acctState] = useRecoilState(AcctState);

  const { initializeAcctState } = useAppState();

  const [initialized, setInitialized] = useState(false);

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useLayoutEffect(() => {
    // URLからカンパニーCDを取得
    const matchs = location.pathname.match(/^\/(ws|setting)\/([^\/]+).*$/);
    const companyCd = matchs && matchs.length == 3 ? matchs[2] : null;

    // 初期読み込みまたは戻るボタンの制御
    if (_isStateNotInitialized()) {
      setInitialized(false);
    }
    (async () => {
      try {
        if (!acctState.initialized) {
          // 認証済みでログイン画面に遷移する場合はホーム画面に遷移
          if ((await initializeAcctState(companyCd)).company != null) {
            if (location.pathname.startsWith('/signin')) {
              navigate('/');
            }
          }
          // 未認証でかつ認証が必要な画面にアクセスする場合はログイン画面に遷移
          else {
            if (_isRequiresAuthentication(location.pathname)) {
              navigate('/signin', {
                state: { referer: location.pathname },
              });
            }
          }
        }
      } catch (e) {
        showBoundary(e);
      } finally {
        setInitialized(true);
      }
    })();
  }, [location.pathname]);

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  const _isRequiresAuthentication = (pathname: string): boolean => {
    if (!pathname) {
      return true;
    }
    if (pathname.startsWith('/ws/')) {
      return true;
    }
    if (pathname.startsWith('/setting/')) {
      return true;
    }

    return false;
  };

  const _isStateNotInitialized = (): boolean => !acctState.initialized;

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {initialized && (
        <Routes>
          {acctState.initialized ? (
            <>
              <Route path="/" element={<Index />} />
              {/* カンパニー選択画面 */}
              <Route path="/signin/company/" element={<SigninCompany />} />
              {/* ログイン認証コード入力画面 */}
              <Route path="/signin/auth/" element={<SigninAuth />} />
              {/* アカウント登録画面 */}
              <Route path="/signup/" element={<Signup />} />
              {/* (旧)アカウント登録画面 */}
              <Route path="/signupOld/" element={<SignupOld />} />
              <Route path="/signupOld/company/" element={<SignupCompanyOld />} />
              {/* 招待受付画面 */}
              <Route path="/invitation/:invitationCd/" element={<InvitationIndex />} />
              {/* 招待受付ユーザー情報入力画面 */}
              <Route path="/invitation/:invitationCd/user" element={<InvitationUser />} />
              {/* 代理ログイン画面 */}
              <Route path="/proxy/:companyCd/" element={<ProxyLogin />} />
              {/* 代理ログイン画面 */}
              <Route path="/proxy/callback/" element={<ProxyCallback />} />
              {/* 404エラー画面 */}
              <Route path="*" element={<Error404 />} />
            </>
          ) : (
            ''
          )}
          {/* ログイン済の場合のみ表示可能な画面 */}
          {acctState.initialized && acctState.company != null ? (
            <>
              <Route path="/setting/:companyCd/" element={<SettingLayout />}>
                {/* プロフィール設定画面 */}
                <Route path="/setting/:companyCd/profile" element={<SettingProfile />} />
                {/* カンパニー設定画面 */}
                <Route path="/setting/:companyCd/company" element={<SettingCompany />} />
                {/* カンパニーメンバー設定画面 */}
                <Route path="/setting/:companyCd/member" element={<SettingMember />} />
                {/* カンパニーメンバー詳細画面 */}
                <Route path="/setting/:companyCd/member/edit" element={<SettingMemberEdit />} />
              </Route>

              <Route path="/ws/:companyCd/" element={<WsLayout />}>
                {/* ホーム画面 */}
                <Route path="/ws/:companyCd/" element={<WsIndex />} />
                {/* コンテンツ画面 */}
                <Route path="/ws/:companyCd/content/search/" element={<WsContentSearch />} />
                <Route path="/ws/:companyCd/content/search/:pageNo" element={<WsContentSearch />} />
                <Route path="/ws/:companyCd/content/search" element={<WsQuiz />} />
                {/* クイズ作成・編集画面 */}
                <Route path="/ws/:companyCd/quiz/edit" element={<WsQuizEdit />} />
                <Route path="/ws/:companyCd/quiz/edit/:quizCd" element={<WsQuizEdit />} />
                {/* 診断作成・編集画面 */}
                <Route path="/ws/:companyCd/diagnosis/edit" element={<WsDiagnosisEdit />} />
                <Route
                  path="/ws/:companyCd/diagnosis/edit/:diagnosisCd"
                  element={<WsDiagnosisEdit />}
                />
                {/* 獲得リスト画面 */}
                <Route path="/ws/:companyCd/respondent/search/" element={<WsRespondentSearch />} />
                <Route
                  path="/ws/:companyCd/respondent/search/:pageNo"
                  element={<WsRespondentSearch />}
                />
                {/* クイズ獲得リスト詳細画面 */}
                <Route
                  path="/ws/:companyCd/respondent/quiz/:quizCd"
                  element={<WsRespondentQuiz />}
                />
                {/* 診断獲得リスト詳細画面 */}
                <Route
                  path="/ws/:companyCd/respondent/diagnosis/:diagnosisCd"
                  element={<WsRespondentDiagnosis />}
                />
                {/* レポート画面 */}
                <Route path="/ws/:companyCd/report/search/" element={<WsReportSearch />} />
                <Route path="/ws/:companyCd/report/search/:pageNo" element={<WsReportSearch />} />
                {/* クイズレポート詳細画面 */}
                <Route path="/ws/:companyCd/report/quiz/:quizCd" element={<WsReportQuiz />} />
                {/* 診断レポート詳細画面 */}
                <Route
                  path="/ws/:companyCd/report/diagnosis/:diagnosisCd"
                  element={<WsReportDiagnosis />}
                />
              </Route>
            </>
          ) : (
            ''
          )}
          {/* 未ログインの場合のみ表示可能な画面 */}
          {acctState.initialized && acctState.company == null ? (
            <>
              {/* ログイン画面 */}
              <Route path="/signin/" element={<Signin />} />
            </>
          ) : (
            ''
          )}
        </Routes>
      )}
    </>
  );
}
